import * as React from "react";

import { Button, Checkbox, Dialog, Field, Textarea } from "@/components/ui";
import { VideoProject } from "@/services/bedrock";

import { buildQaIssues, EMPTY_QA_ISSUE_DRAFT, QaIssueDraft } from "./qaIssues";

interface VideoProjectRejectQaDialogProps {
  open: boolean;
  playerName?: string | null;
  onConfirm: (issues: VideoProject.QaIssue[]) => void;
  onCancel: () => void;
}

export function VideoProjectRejectQaDialog({ open, playerName, onConfirm, onCancel }: VideoProjectRejectQaDialogProps) {
  const [draft, setDraft] = React.useState<QaIssueDraft>(EMPTY_QA_ISSUE_DRAFT);

  React.useEffect(() => {
    if (open) setDraft(EMPTY_QA_ISSUE_DRAFT);
  }, [open]);

  const issues = buildQaIssues(draft);
  const subject = playerName?.trim() || "this project";

  const toggleCategory = (category: VideoProject.QaIssueCategory, checked: boolean) => {
    setDraft(current => ({
      ...current,
      selected: { ...current.selected, [category]: checked },
    }));
  };

  const setComment = (category: VideoProject.QaIssueCategory, comment: string) => {
    setDraft(current => ({
      ...current,
      comments: { ...current.comments, [category]: comment },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onCancel()} size="md">
      <Dialog.Header>
        <Dialog.Title>Fail QA</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-4">
        <Dialog.Description>
          Return {subject} to Editing and record why it failed. Select at least one issue.
        </Dialog.Description>
        <Field.Set>
          <Field.Legend className="sr-only">QA issues</Field.Legend>
          <Field.Group className="gap-3">
            {VideoProject.QaIssueCategories.map(category => {
              const selected = Boolean(draft.selected[category]);
              return (
                <div key={category} className="flex flex-col gap-2">
                  <Checkbox
                    checked={selected}
                    label={VideoProject.QaIssueCategoryMeta[category].label}
                    onCheckedChange={checked => toggleCategory(category, checked === true)}
                  />
                  {selected ? (
                    <Field className="pl-6" label="Comment">
                      {controlProps => (
                        <Textarea
                          {...controlProps}
                          autoSize
                          minRows={2}
                          maxRows={6}
                          value={draft.comments[category] ?? ""}
                          placeholder="What should be fixed?"
                          onChange={event => setComment(category, event.target.value)}
                        />
                      )}
                    </Field>
                  ) : null}
                </div>
              );
            })}
          </Field.Group>
        </Field.Set>
      </Dialog.Content>
      <Dialog.Footer>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="destructive" disabled={issues.length === 0} onClick={() => onConfirm(issues)}>
          Fail QA
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}

export function useVideoProjectRejectQaDialog() {
  const [prompt, setPrompt] = React.useState<{
    playerName?: string | null;
    resolve: (issues: VideoProject.QaIssue[] | null) => void;
  } | null>(null);

  const requestIssues = React.useCallback((playerName?: string | null) => {
    return new Promise<VideoProject.QaIssue[] | null>(resolve => {
      setPrompt(current => {
        current?.resolve(null);
        return { playerName, resolve };
      });
    });
  }, []);

  const close = React.useCallback((issues: VideoProject.QaIssue[] | null) => {
    setPrompt(current => {
      current?.resolve(issues);
      return null;
    });
  }, []);

  const dialog = (
    <VideoProjectRejectQaDialog
      open={prompt != null}
      playerName={prompt?.playerName}
      onConfirm={issues => close(issues)}
      onCancel={() => close(null)}
    />
  );

  return { requestIssues, dialog };
}
